import type {
  DocumentationPipelineStatus,
  MetricComputationView,
  MetricLegajo,
  TechnicalSpecificationState
} from '../../services/projectAutofillDraftService.ts'
import type { DraftMetadataFileEntry } from './projectAutofillDraftUtils.ts'

export const BUDGET_SHEET_REQUIRES_METRIC_COMPUTATION_CODE =
  'BUDGET_SHEET_REQUIRES_METRIC_COMPUTATION'

export const DOCUMENTATION_WORKFLOW_INCOMPLETE_CODE = 'DOCUMENTATION_WORKFLOW_INCOMPLETE'

export type ReviewStage = 'none' | 'quotation_review' | 'metric_only'

export type SheetGenerationOutcome = 'pending' | 'succeeded' | 'failed'

export type DocumentationReviewContext = {
  reviewStage: ReviewStage | null
  sheetGenerationOutcome: SheetGenerationOutcome | null
  isProcessing: boolean
  isQuotationReview: boolean
  isMetricOnly: boolean
  canPublishReview: boolean
}

export type MetricEligibility = 'ready' | 'missing'

export type QuotationEligibility =
  | 'locked'
  | 'pending'
  | 'skipped_no_metric'
  | 'quotation_generation_failed'
  | 'generated'
  | 'user_edited'

export type TechnicalSpecEligibility =
  | 'locked'
  | 'pdf_legajo'
  | 'generated'
  | 'user_edited'
  | 'generation_failed'

export function parseMetricEligibility(raw: unknown): MetricEligibility | null {
  if (raw === 'ready' || raw === 'missing') return raw
  return null
}

export function parseTechnicalSpecEligibility(raw: unknown): TechnicalSpecEligibility | null {
  const allowed: TechnicalSpecEligibility[] = [
    'locked',
    'pdf_legajo',
    'generated',
    'user_edited',
    'generation_failed'
  ]
  if (typeof raw === 'string' && (allowed as string[]).includes(raw)) {
    return raw as TechnicalSpecEligibility
  }
  return null
}

export function parseQuotationEligibility(raw: unknown): QuotationEligibility | null {
  const allowed: QuotationEligibility[] = [
    'locked',
    'pending',
    'skipped_no_metric',
    'quotation_generation_failed',
    'generated',
    'user_edited'
  ]
  if (typeof raw === 'string' && (allowed as string[]).includes(raw)) {
    return raw as QuotationEligibility
  }
  return null
}

export function parseReviewStage(raw: unknown): ReviewStage | null {
  if (raw === 'none' || raw === 'quotation_review' || raw === 'metric_only') return raw
  return null
}

export function parseSheetGenerationOutcome(raw: unknown): SheetGenerationOutcome | null {
  if (raw === 'pending' || raw === 'succeeded' || raw === 'failed') return raw
  return null
}

export function extractReviewStageFromMetadata(metadata: unknown): ReviewStage | null {
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) return null
  return parseReviewStage((metadata as Record<string, unknown>).documentationReviewStage)
}

export function extractSheetGenerationOutcomeFromMetadata(
  metadata: unknown
): SheetGenerationOutcome | null {
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) return null
  return parseSheetGenerationOutcome(
    (metadata as Record<string, unknown>).documentationSheetGenerationOutcome
  )
}

export function readyForReviewFromDraftMetadata(metadata: unknown): boolean {
  return (
    extractReviewStageFromMetadata(metadata) === 'quotation_review' &&
    extractSheetGenerationOutcomeFromMetadata(metadata) === 'succeeded'
  )
}

export function isDocumentationPipelinePollComplete(status: DocumentationPipelineStatus): boolean {
  return status.phase === 'ready' && status.readyForReview === true
}

export function isDocumentationPipelineProcessingPhase(pipelinePhase: string | null | undefined): boolean {
  return pipelinePhase === 'processing' || pipelinePhase === 'awaiting_analysis'
}

export function buildDocumentationReviewContext(
  metadata: unknown,
  pipelinePhase?: string | null
): DocumentationReviewContext {
  const reviewStage = extractReviewStageFromMetadata(metadata)
  const sheetGenerationOutcome = extractSheetGenerationOutcomeFromMetadata(metadata)
  const phase =
    typeof pipelinePhase === 'string' && pipelinePhase.trim() ? pipelinePhase.trim() : null
  let isProcessing = isDocumentationPipelineProcessingPhase(phase)
  if (!isProcessing && reviewStage === 'none' && sheetGenerationOutcome === 'pending') {
    isProcessing = true
  }
  if (!isProcessing && metadata && typeof metadata === 'object' && !Array.isArray(metadata)) {
    const m = metadata as Record<string, unknown>
    if (m.documentationAnalysisStatus === 'running') isProcessing = true
  }
  const isQuotationReview = reviewStage === 'quotation_review'
  const isMetricOnly = reviewStage === 'metric_only'
  const canPublishReview = reviewStage === 'quotation_review'
  return {
    reviewStage,
    sheetGenerationOutcome,
    isProcessing,
    isQuotationReview,
    isMetricOnly,
    canPublishReview
  }
}

export function isMetricOnlyPipelineOutcome(status: DocumentationPipelineStatus): boolean {
  return (
    status.phase === 'ready' &&
    status.readyForReview !== true &&
    status.reviewStage === 'metric_only'
  )
}

export function isDocumentationPipelineProcessing(status: DocumentationPipelineStatus): boolean {
  return (
    isDocumentationPipelineProcessingPhase(status.phase) ||
    (status.reviewStage === 'none' && status.sheetGenerationOutcome === 'pending')
  )
}

export function canPublishByReviewStage(reviewStage: ReviewStage | null): boolean {
  return reviewStage === 'quotation_review'
}

export function isQuotationReviewScreen(reviewStage: ReviewStage | null): boolean {
  return reviewStage === 'quotation_review'
}

export function isMetricOnlyReviewScreen(reviewStage: ReviewStage | null): boolean {
  return reviewStage === 'metric_only'
}

export function parseMetricComputationView(raw: unknown): MetricComputationView | null {
  if (raw === 'editable_grid' || raw === 'legajo' || raw === 'missing') return raw
  return null
}

export function parseMetricLegajo(raw: unknown): MetricLegajo | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined
  const o = raw as Record<string, unknown>
  const fileId = o.fileId != null ? String(o.fileId).trim() : ''
  if (!fileId) return undefined
  const pagesRaw = o.pages
  const pages: number[] = []
  if (Array.isArray(pagesRaw)) {
    for (const p of pagesRaw) {
      const n = typeof p === 'number' ? p : Number(p)
      if (Number.isFinite(n) && n >= 1) pages.push(Math.floor(n))
    }
  }
  return {
    fileId,
    originalName: o.originalName != null ? String(o.originalName).trim() || undefined : undefined,
    mimeType: o.mimeType != null ? String(o.mimeType).trim() || undefined : undefined,
    storageUrl: o.storageUrl != null ? String(o.storageUrl).trim() || undefined : undefined,
    pages,
    isSpreadsheet: o.isSpreadsheet === true
  }
}

export function resolveMetricLegajoStorageUrl(
  legajo: MetricLegajo | undefined | null,
  draftFiles?: DraftMetadataFileEntry[]
): string | undefined {
  const direct = legajo?.storageUrl?.trim()
  if (direct) return direct
  const fid = legajo?.fileId?.trim()
  if (!fid || !draftFiles?.length) return undefined
  const entry = draftFiles.find((f) => String(f.id ?? '') === fid)
  return entry?.storageUrl?.trim() || undefined
}

export function rubroHasMetricComputationFileId(row: {
  documentationFileIds?: { metricComputation?: string }
}): boolean {
  const fid = row.documentationFileIds?.metricComputation
  return typeof fid === 'string' && fid.trim().length > 0
}

export function rubroHasMetricComputationPages(slots: { metricComputation?: number[] }): boolean {
  const pages = slots.metricComputation
  return Array.isArray(pages) && pages.length > 0
}

export function metricSheetHasUsableRows(
  state: { rows?: { cells?: Record<string, unknown> }[] } | undefined | null
): boolean {
  if (!state?.rows?.length) return false
  for (const row of state.rows) {
    const item = row.cells?.item
    if (item != null && String(item).trim() !== '') return true
  }
  return false
}

export function resolveMetricComputationView(row: {
  metricComputationView?: MetricComputationView | null
  metricSheetState?: { rows?: { cells?: Record<string, unknown> }[] } | null
  documentationSlots?: { metricComputation?: number[] }
  documentationFileIds?: { metricComputation?: string }
}): MetricComputationView {
  const fromApi = parseMetricComputationView(row.metricComputationView)
  if (fromApi) return fromApi
  if (metricSheetHasUsableRows(row.metricSheetState)) return 'editable_grid'
  const slots = row.documentationSlots ?? {}
  if (rubroHasMetricComputationPages(slots) || rubroHasMetricComputationFileId(row)) {
    return 'legajo'
  }
  return 'missing'
}

export function rubroMetricReadyFromRow(row: {
  metricComputationView?: MetricComputationView | null
  metricEligibility?: MetricEligibility | null
  documentationSlots?: { metricComputation?: number[] }
  documentationFileIds?: { metricComputation?: string }
  metricSheetState?: { rows?: { cells?: Record<string, unknown> }[] } | null
}): boolean {
  const view = resolveMetricComputationView(row)
  if (view === 'editable_grid' || view === 'legajo') {
    if (row.metricEligibility === 'missing') return false
    return true
  }
  if (row.metricEligibility === 'ready') return true
  const slots = row.documentationSlots ?? {}
  const hasLocal =
    rubroHasMetricComputationPages(slots) ||
    rubroHasMetricComputationFileId(row) ||
    metricSheetHasUsableRows(row.metricSheetState)
  if (!hasLocal) return false
  if (row.metricEligibility === 'missing') return false
  return true
}

export function isDraftDocumentationAnalyzed(metadata: unknown): boolean {
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) return false
  const m = metadata as Record<string, unknown>
  if (m.analyzed === true) return true
  if (m.documentationAnalysisStatus === 'succeeded') return true
  return readyForReviewFromDraftMetadata(metadata)
}

export function extractRubroMetricWorkflowFields(
  spRec: Record<string, unknown>
): {
  metricComputationView?: MetricComputationView
  metricLegajo?: MetricLegajo
  canImportMetricEdifySheet?: boolean
} {
  const view = parseMetricComputationView(spRec.metricComputationView)
  const legajo = parseMetricLegajo(spRec.metricLegajo)
  const canImport = spRec.canImportMetricEdifySheet === true
  return {
    ...(view ? { metricComputationView: view } : {}),
    ...(legajo ? { metricLegajo: legajo } : {}),
    ...(canImport ? { canImportMetricEdifySheet: true } : {})
  }
}

export function rubroBudgetLegajoInPdf(row: {
  documentationSlots?: { budget?: number[] }
  documentationFileIds?: { budget?: string }
}): boolean {
  const pages = row.documentationSlots?.budget
  if (Array.isArray(pages) && pages.length > 0) return true
  const fid = row.documentationFileIds?.budget
  return typeof fid === 'string' && fid.trim().length > 0
}

export function canShowQuotationSheetUi(
  quotationEligibility: QuotationEligibility | null | undefined,
  review: DocumentationReviewContext | Pick<DocumentationReviewContext, 'isMetricOnly' | 'isQuotationReview' | 'isProcessing'>
): boolean {
  if (review.isMetricOnly) return false
  if (!review.isQuotationReview && review.isProcessing) return false
  if (quotationEligibility === 'locked' || quotationEligibility === 'pending') return false
  return (
    quotationEligibility === 'generated' ||
    quotationEligibility === 'user_edited' ||
    quotationEligibility === 'quotation_generation_failed' ||
    quotationEligibility === 'skipped_no_metric'
  )
}

export function canEditBudgetSheetState(
  quotationEligibility: QuotationEligibility | null | undefined,
  reviewStage: ReviewStage | null
): boolean {
  if (reviewStage !== 'quotation_review') return false
  return quotationEligibility === 'generated' || quotationEligibility === 'user_edited'
}

export function isQuotationTabEnabled(reviewStage: ReviewStage | null): boolean {
  return reviewStage === 'quotation_review'
}

export function rubroTechnicalSpecLegajoInPdf(row: {
  documentationSlots?: { technicalSpecifications?: number[] }
  documentationFileIds?: { technicalSpecifications?: string }
}): boolean {
  const pages = row.documentationSlots?.technicalSpecifications
  if (Array.isArray(pages) && pages.length > 0) return true
  const fid = row.documentationFileIds?.technicalSpecifications
  return typeof fid === 'string' && fid.trim().length > 0
}

export function technicalSpecHasUsableContent(
  state: TechnicalSpecificationState | undefined | null
): boolean {
  if (!state?.sections?.length) return false
  return state.sections.some((s) => String(s.body ?? '').trim().length > 0)
}

export function isTechnicalSpecTabEnabled(
  review: Pick<DocumentationReviewContext, 'reviewStage' | 'isProcessing'>,
  eligibility: TechnicalSpecEligibility | null | undefined
): boolean {
  if (eligibility === 'locked') return false
  if (review.isProcessing) return false
  return review.reviewStage === 'quotation_review' || review.reviewStage === 'metric_only'
}

export function canEditTechnicalSpecState(
  eligibility: TechnicalSpecEligibility | null | undefined,
  reviewStage: ReviewStage | null
): boolean {
  if (reviewStage !== 'quotation_review') return false
  if (eligibility === 'pdf_legajo' || eligibility === 'locked') return false
  return eligibility === 'generated' || eligibility === 'user_edited' || eligibility === 'generation_failed'
}

export function canShowTechnicalSpecEditor(
  eligibility: TechnicalSpecEligibility | null | undefined
): boolean {
  return eligibility === 'generated' || eligibility === 'user_edited' || eligibility === 'generation_failed'
}

export function parseProjectAutofillApiError(err: unknown): {
  code?: string
  message: string
  tempId?: string
  rubroLabel?: string
} {
  const fallback = { message: 'Ocurrió un error. Intentá de nuevo.' }
  if (!err || typeof err !== 'object') return fallback
  const axiosErr = err as { response?: { data?: unknown }; message?: string }
  const body = axiosErr.response?.data
  if (!body || typeof body !== 'object') {
    return { message: typeof axiosErr.message === 'string' ? axiosErr.message : fallback.message }
  }
  const o = body as Record<string, unknown>
  const message =
    typeof o.message === 'string' && o.message.trim()
      ? o.message.trim()
      : fallback.message
  const code = typeof o.code === 'string' ? o.code : undefined
  const tempId = typeof o.tempId === 'string' ? o.tempId : undefined
  const rubroLabel = typeof o.rubroLabel === 'string' ? o.rubroLabel : undefined
  return { code, message, tempId, rubroLabel }
}
